import { useTranslation } from 'react-i18next'
import { MONTAGE_UEBERSCHRIFT, montageVerfahrenText, type MontageUmfang } from '@symphenia/shared-types'
import type { ErpVorzeigeProps } from '../registry'

interface KanonPunkt {
  key: string
  gruppe: string | null
  text: string
  art: string
  ergebnis: 'OK' | 'NOK' | 'NA' | null
  ueberschrieben: boolean
  messwert: string | null
  einheit: string | null
  soll: string | null
  tolMinus: string | null
  tolPlus: string | null
  angabe: string | null
  bemerkung: string | null
  fotos: string[]
}

interface KanonBauteil {
  key: string
  bezeichnung: string
  menge: string | null
  exc: string | null
  detailgrad: string
  vorlage: { name: string; fassung: number } | null
  punkte: KanonPunkt[]
  anziehprotokoll: { key: string; verbindung: string; garnitur: string; anzahl: number; verfahren: string; sollMoment: string | null; pruefmittel: string | null; kalibriertBis: string | null; geprueft: number | null; ergebnis: 'OK' | 'NOK' | null; bemerkung: string | null }[]
  vermessung: { key: string; punkt: string; richtung: string; soll: string | null; ist: string | null; einheit: string; toleranz: string | null; abweichung: string | null; ergebnis: 'OK' | 'NOK' | null; ueberschrieben: boolean; bemerkung: string | null }[]
}

export interface KanonMontage {
  umfang: MontageUmfang
  zeitraum: { von: string; bis: string } | null
  ausfuehrende: string[]
  bauteile: KanonBauteil[]
  maengel: { nr: number; bauteilKey: string | null; text: string; gewicht: 'WESENTLICH' | 'UNWESENTLICH' | null; frist: string | null; fotos: string[] }[]
  abnahme: { ergebnis: string | null; teilabnahme: boolean; vorbehalt: string | null; bezug: { nummer: string; pruefwert: string }[]; behoben: { nummer: string; nr: number; text: string }[] } | null
  bemerkung: string | null
}

/** `12.500` → `12,5` — der Fachteil trägt drei Stellen, die Anzeige keine überflüssigen Nullen. */
export const zahl = (t: string | null | undefined) => (t ? t.replace(/\.?0+$/, '').replace('.', ',') : '')
export const datumDe = (iso: string | null | undefined) => (iso ? iso.slice(0, 10).split('-').reverse().join('.') : '')

/**
 * AP-47 11.2 — **Vorzeige des Montageprotokolls im ERP**: aus dem kanonischen Fachteil (AP-46 9.), wie in der
 * PWA — Überschrift nach Umfang, Bauteile mit Prüfpunkten, Anziehprotokoll und Vermessung, Mängel, Abnahme.
 * n. i. O. und überschriebene Ergebnisse hervorgehoben.
 */
export function MontageErpVorzeige({ fachteil }: ErpVorzeigeProps) {
  const { t } = useTranslation()
  const f = fachteil as unknown as KanonMontage
  const namen = new Map(f.bauteile.map((b) => [b.key, b.bezeichnung]))
  const zelle = 'px-2 py-1 align-top'
  const kopf = 'px-2 py-1 text-left text-xs font-medium text-muted-foreground'
  const erg = (e: string | null) => (e ? t(`siteDocs.assembly.results.${e}`) : '')
  return (
    <div className="space-y-4 text-sm">
      <h2 className="text-base font-semibold">{MONTAGE_UEBERSCHRIFT[f.umfang]}</h2>
      {f.zeitraum && <p>{t('siteDocs.assembly.period')}: {f.zeitraum.von === f.zeitraum.bis ? datumDe(f.zeitraum.von) : `${datumDe(f.zeitraum.von)} – ${datumDe(f.zeitraum.bis)}`}</p>}
      {f.ausfuehrende.length > 0 && <p>{t('siteDocs.assembly.performedBy')}: {f.ausfuehrende.join(', ')}</p>}
      {f.bauteile.map((b) => (
        <section key={b.key} className="space-y-1 rounded-md border p-2">
          <h3 className="font-medium">{b.bezeichnung}</h3>
          <p className="text-xs text-muted-foreground">
            {[b.menge, b.exc, f.umfang !== 'ABNAHME' ? t(`siteDocs.assembly.levels.${b.detailgrad}`) : null, b.vorlage ? `${b.vorlage.name} (${t('siteDocs.assembly.version')} ${b.vorlage.fassung})` : null].filter(Boolean).join(' · ')}
          </p>
          {b.punkte.length > 0 && (
            <table className="w-full">
              <thead>
                <tr>
                  <th className={kopf}>{t('siteDocs.assembly.group')}</th>
                  <th className={kopf}>{t('siteDocs.assembly.point')}</th>
                  <th className={kopf}>{t('siteDocs.assembly.valueEntry')}</th>
                  <th className={kopf}>{t('siteDocs.assembly.result')}</th>
                  <th className={kopf}>{t('siteDocs.assembly.remark')}</th>
                </tr>
              </thead>
              <tbody>
                {b.punkte.map((p) => (
                  <tr key={p.key} className={p.ergebnis === 'NOK' || p.ueberschrieben ? 'font-semibold text-red-800 dark:text-red-300' : ''}>
                    <td className={zelle}>{p.gruppe}</td>
                    <td className={zelle}>{p.text}</td>
                    <td className={zelle}>
                      {p.art === 'TEXT' ? p.angabe : p.messwert ? `${zahl(p.messwert)} ${p.einheit ?? ''}${p.soll ? ` (${t('siteDocs.assembly.target')} ${zahl(p.soll)} −${zahl(p.tolMinus) || '0'}/+${zahl(p.tolPlus) || '0'})` : ''}` : ''}
                    </td>
                    <td className={zelle}>
                      {erg(p.ergebnis)}
                      {p.ueberschrieben ? ` (${t('siteDocs.assembly.overridden')})` : ''}
                    </td>
                    <td className={zelle}>{p.bemerkung}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {b.anziehprotokoll.length > 0 && (
            <>
              <h4 className="pt-1 text-xs font-semibold uppercase text-muted-foreground">{t('siteDocs.assembly.torque')}</h4>
              <ul className="space-y-0.5">
                {b.anziehprotokoll.map((z) => (
                  <li key={z.key} className={z.ergebnis === 'NOK' ? 'font-semibold text-red-800 dark:text-red-300' : ''}>
                    {z.verbindung} · {z.garnitur} · {z.anzahl} × {montageVerfahrenText(z.verfahren)}
                    {z.sollMoment ? ` · ${zahl(z.sollMoment)} Nm` : ''}
                    {z.pruefmittel ? ` · ${z.pruefmittel}` : ''}
                    {z.kalibriertBis ? ` (${t('siteDocs.assembly.calibratedUntil')} ${datumDe(z.kalibriertBis)})` : ''} — {erg(z.ergebnis)}
                    {z.bemerkung ? ` — ${z.bemerkung}` : ''}
                  </li>
                ))}
              </ul>
            </>
          )}
          {b.vermessung.length > 0 && (
            <>
              <h4 className="pt-1 text-xs font-semibold uppercase text-muted-foreground">{t('siteDocs.assembly.survey')}</h4>
              <ul className="space-y-0.5">
                {b.vermessung.map((z) => (
                  <li key={z.key} className={z.ergebnis === 'NOK' || z.ueberschrieben ? 'font-semibold text-red-800 dark:text-red-300' : ''}>
                    {z.punkt} · {t(`siteDocs.assembly.directions.${z.richtung}`)} · {t('siteDocs.assembly.target')} {zahl(z.soll) || '0'} · {t('siteDocs.assembly.actual')} {zahl(z.ist) || '0'} · {t('siteDocs.assembly.deviation')} {zahl(z.abweichung) || '0'} {z.einheit} (± {zahl(z.toleranz)}) — {erg(z.ergebnis)}
                    {z.ueberschrieben ? ` (${t('siteDocs.assembly.overridden')})` : ''}
                    {z.bemerkung ? ` — ${z.bemerkung}` : ''}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      ))}
      <section className="space-y-1">
        <h3 className="font-medium">{t('siteDocs.assembly.defects')}</h3>
        {f.maengel.length ? (
          <ul className="divide-y rounded-md border">
            {f.maengel.map((m) => (
              <li key={m.nr} className="px-2 py-1">
                <strong>{m.nr}.</strong> {m.text}
                <span className="ml-2 text-xs text-muted-foreground">
                  {[m.bauteilKey ? namen.get(m.bauteilKey) : t('siteDocs.assembly.general'), m.gewicht ? t(`siteDocs.assembly.weights.${m.gewicht}`) : null, m.frist ? `${t('siteDocs.assembly.due')} ${datumDe(m.frist)}` : null].filter(Boolean).join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground">{t('siteDocs.assembly.noDefects')}</p>
        )}
      </section>
      {f.abnahme && (
        <section className="space-y-1 rounded-md border-2 border-primary/40 p-2">
          <h3 className="font-medium">{t('siteDocs.assembly.acceptance')}</h3>
          <p className="text-base font-bold">{f.abnahme.ergebnis ? t(`siteDocs.assembly.acceptanceResults.${f.abnahme.ergebnis}`) : '—'}</p>
          {f.abnahme.teilabnahme && <p>{t('siteDocs.assembly.partial')}</p>}
          {f.abnahme.vorbehalt && <p className="whitespace-pre-line">{f.abnahme.vorbehalt}</p>}
          {f.abnahme.bezug.length > 0 && (
            <p>
              {t('siteDocs.assembly.refersTo')}: {f.abnahme.bezug.map((b) => `${b.nummer} (${b.pruefwert.slice(0, 8)})`).join(', ')}
            </p>
          )}
          {f.abnahme.behoben.map((b) => (
            <p key={`${b.nummer}-${b.nr}`}>{t('siteDocs.assembly.fixedLine', { nummer: b.nummer, nr: b.nr, text: b.text })}</p>
          ))}
        </section>
      )}
      {f.bemerkung && <p className="whitespace-pre-line">{f.bemerkung}</p>}
    </div>
  )
}
